/* eslint-disable react/jsx-max-depth */
// We might have a lot of provider, indentation levels are not a problem here
import { Suspense, useEffect } from 'react';

import { AppShell } from '@mantine/core';
import { modals } from '@mantine/modals';

import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { BrowserRouter, Outlet, useLocation } from 'react-router-dom';
import { SuperTokensWrapper } from 'supertokens-auth-react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';

import { Error } from '../shared/components/UI/Error/Error.tsx';
import { Loader } from '../shared/components/UI/Loader/Loader.tsx';
import { Navbar } from '../shared/components/UI/Navbar/Navbar.tsx';

import * as superTokens from '../shared/infra/supertokens.ts';
import { setOnboardingFlag } from '../shared/api/magellan/users/index.ts';
import { ThemeProvider } from '../shared/contexts/MantineTheme.provider.tsx';
import ModalsProvider from '../shared/contexts/Modals.provider.tsx';
import { SidepanelProvider } from '../shared/contexts/Sidepanel.provider.tsx';
import { UrlHashProvider } from '../shared/contexts/UrlHash.provider.tsx';
import { useActiveUserQuery } from '../shared/hooks/useActiveUserQuery.hook';
import { posthogClient } from '../shared/infra/posthogClient.ts';
import { queryClient } from '../shared/infra/queryClient.ts';
import { Router } from './Router.tsx';

superTokens.init();

export default function App() {
  return (
    <ThemeProvider>
      <SuperTokensWrapper>
        <QueryClientProvider client={queryClient}>
          <PostHogProvider client={posthogClient}>
            <BrowserRouter>
              <ModalsProvider>
                <UrlHashProvider>
                  <SidepanelProvider>
                    <Router />
                  </SidepanelProvider>
                </UrlHashProvider>
              </ModalsProvider>
            </BrowserRouter>
          </PostHogProvider>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
        </QueryClientProvider>
      </SuperTokensWrapper>
    </ThemeProvider>
  );
}

export function AppWithLogin() {
  const session = useSessionContext();
  const activeUserQuery = useActiveUserQuery();
  const location = useLocation();
  const posthog = usePostHog();

  const searchParams = new URLSearchParams(location.search);
  const onboarding = searchParams.get('onboarding');

  // Track user and workspace information in posthog
  useEffect(() => {
    if (!session.loading && session.doesSessionExist) {
      if (import.meta.env.PROD) {
        const supertokensUserId = session.userId;
        const email = session.accessTokenPayload.email;
        const firstName = session.accessTokenPayload.firstName;
        const lastName = session.accessTokenPayload.lastName;
        const workspaceUniqueName =
          session.accessTokenPayload.workspaceUniqueName;
        const workspaceDisplayName =
          session.accessTokenPayload.workspaceDisplayName;

        posthog.identify(supertokensUserId, {
          email,
          ignoreInReporting: activeUserQuery.data.ignoreInReporting,
          firstName,
          lastName,
        });

        posthog.group('workspace', workspaceUniqueName, {
          workspaceUniqueName,
          workspaceDisplayName,
        });
      }
    }
  }, [activeUserQuery.data.ignoreInReporting, session, posthog]);

  // Track pageviews in posthog
  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
    });
  }, [location, posthog]);

  useEffect(() => {
    if (onboarding === 'true') {
      openOnboardingModal();
    }
  }, [onboarding]);

  const openOnboardingModal = () => {
    modals.openContextModal({
      fullScreen: true,
      withCloseButton: true,
      onClose: () => {
        setOnboardingFlag(true);
      },
      modal: 'onboarding',
      transitionProps: {
        transition: 'slide-up',
        duration: 300,
        timingFunction: 'linear',
      },
      innerProps: { step: 0 },
    });
  };

  return (
    <AppShell padding={0} header={<Navbar />}>
      <Sentry.ErrorBoundary fallback={<Error />}>
        <Suspense fallback={<Loader title="Chargement" />}>
          <Outlet />
        </Suspense>
      </Sentry.ErrorBoundary>
    </AppShell>
  );
}
